<template>
  <component :is="as" :class="classes" :tabindex="interactive ? 0 : undefined">
    <slot />
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { cva, type VariantProps } from 'class-variance-authority';
import type { CardAs } from '@/types/component-types';

const cardVariants = cva('', {
  variants: {
    tone: {
      default: 'bg-paper border border-stroke',
      raised: 'bg-paper border border-stroke shadow-md',
      muted: 'bg-paper-muted border border-stroke',
      accent: 'bg-accent-soft border border-stroke text-accent-strong',
    },
    padding: {
      sm: 'p-4',
      md: 'p-6',
      lg: 'p-8',
    },
    radius: {
      md: 'rounded-md',
      lg: 'rounded-lg',
      xl: 'rounded-xl',
      '2xl': 'rounded-2xl',
    },
    interactive: {
      true: 'cursor-pointer transition-shadow duration-150 hover:shadow-md focus-visible:shadow-[var(--shadow-ring-focus)] focus-visible:outline-none',
      false: '',
    },
  },
  defaultVariants: {
    tone: 'default',
    padding: 'md',
    radius: '2xl',
    interactive: false,
  },
});

export type CardTone = NonNullable<VariantProps<typeof cardVariants>['tone']>;
export type CardPadding = NonNullable<VariantProps<typeof cardVariants>['padding']>;
export type CardRadius = NonNullable<VariantProps<typeof cardVariants>['radius']>;
export type { CardAs } from '@/types/component-types';

interface Props {
  tone?: CardTone;
  padding?: CardPadding;
  radius?: CardRadius;
  interactive?: boolean;
  as?: CardAs;
}

const props = withDefaults(defineProps<Props>(), {
  tone: 'default',
  padding: 'md',
  radius: '2xl',
  interactive: false,
  as: 'div',
});

const classes = computed(() =>
  cardVariants({
    tone: props.tone,
    padding: props.padding,
    radius: props.radius,
    interactive: props.interactive,
  }),
);
</script>
